/**
 * Broker messages -> a fleet model. Pure: no network, no DOM, no clock.
 *
 * Mirrors the channels `cloud/keenon-adapter/keenon_adapter/normalize.py` publishes, and
 * nothing else. If a channel appears here that the normalizer does not produce, one of
 * the two is wrong.
 *
 * NO CLIENT-SIDE SCOPE FILTERING, deliberately. The app subscribes to `sites/+/robots/+/#`
 * and renders whatever arrives; robots outside the user's grant simply never arrive,
 * because the broker enforces the ACL claim inside their token. Filtering here would be
 * UI that *looks* like security — and it would diverge the moment a grant changed.
 */

export type RobotId = string;

export interface Robot {
  siteId: string;
  robotId: RobotId;
  online?: boolean;
  // The two axes of status/mode: the JOB and its LIFECYCLE. One channel for a Keenon
  // and a Go2, since the fleet contract merged the vendor task enum into status/mode.
  mode?: string;
  modeState?: string;
  // The vendor's own enum, for the debug readout only — never a rendering decision.
  subState?: number;
  batteryPct?: number;
  position?: {
    x: number;
    y: number;
    yaw: number;
    /**
     * Unix seconds at which the PLATFORM says the robot was there, when it says so.
     *
     * Not when we heard it. The C40 reports a `lastReportTimestamp` that has been
     * observed five minutes behind the query — and a five-minute-old dot drawn as
     * current is not a missing robot, it is one confidently in the wrong place.
     */
    reportedAt?: number;
    floor?: string;
    // Which map frame these coordinates are in. `map` is a Norbot map; anything else
    // (e.g. `keenon_map`) is the vendor's own and must NOT be plotted on ours.
    frame?: string;
  };
  fault?: { faulting: boolean; scram: boolean; detail?: string | null };
  model?: string;
  /**
   * Recent `telemetry/vendor_raw` records, NEWEST FIRST and bounded.
   *
   * Kept at all only for the drill-down: the fleet table would be worse for showing it,
   * but "what did the vendor actually send" is the question a detail view exists to
   * answer, and it is the one the generic channels cannot — by design, since they are a
   * reading of the vendor's words rather than the words.
   *
   * Bounded because this is the highest-rate channel in the system. A ring, not a log:
   * a browser left open overnight must not grow without limit.
   */
  raw?: RawRecord[];
  /** How many raw records have arrived per source, e.g. `webhook` / `poll`. Counts the
   *  WHOLE session, unlike `raw`, so the ring turning over does not reset the tally. */
  rawCounts?: Record<string, number>;
  /** Arrival time in this browser, ms. Never a payload stamp — see below. */
  lastSeen: number;
  /**
   * When the STATE was true, ms, from the producer's own stamp — as opposed to when we
   * heard about it. Set from a fleet listing, which is only as current as the last time
   * somebody asked for one.
   *
   * These are different numbers and the difference is the whole point. Polling is
   * request-driven now, so a row can arrive this second describing a robot as it was
   * twenty minutes ago. Rendering `lastSeen` alone would present that as live.
   */
  asOf?: number;
  /** `poll:...` or `webhook:...` — how this row was last learned. */
  source?: string;
  /**
   * What this robot can be asked to DO, from `status/capabilities`.
   *
   * A known-absent capability arrives listed with `enabled: false` and a reason rather
   * than omitted — the contract is explicit about that, because a greyed control that
   * explains itself beats a missing one the operator cannot ask about.
   */
  capabilities?: Capability[];
  /** Which adapter produced the capability document, e.g. `keenon-c40`. Shown beside the
   *  operation count so "is this the build I just deployed?" is answerable from the
   *  screen rather than from `fly status`. */
  platform?: string;
  /**
   * What it will accept RIGHT NOW, from `status/permitted`. Absent from here but present
   * in `capabilities` means "not in this state"; absent from both means "not on this
   * robot". Keenon gates by `subState` — pause only while navigating/cleaning/returning,
   * recharge only from idle, finish only from paused.
   *
   * `undefined` is NOT "nothing is permitted": it means the robot has never published
   * one, and the two must not be confused — the first is a robot saying no, the second
   * is a robot saying nothing.
   */
  permitted?: string[];
  /** The last few `telemetry/command` events, newest first. An action's outcome arrives
   *  here rather than as a reply, so this is the only place a refusal is visible. */
  commands?: CommandEvent[];
}

export interface Capability {
  name: string;
  title?: string;
  enabled?: boolean;
  reason?: string;
  /** When this operation is accepted, in a sentence. `status/permitted` says whether it
   *  is allowed now; this says what would make it so. */
  when?: string;
  /** A caveat about an operation that IS available. Distinct from `reason`, which says
   *  why one is not — this one is offered and carries a warning. */
  notes?: string;
  params?: Record<string, unknown>;
}

export interface CommandEvent {
  ts: number;
  name: string;
  /** accepted | progress | complete | failed | rejected */
  phase: string;
  detail?: string;
  code?: number | string;
  command_id?: string;
}

/** Bounded like the raw ring, and for the same reason. */
export const COMMAND_LIMIT = 12;

/** One verbatim vendor payload, as it arrived. */
export interface RawRecord {
  /** OUR arrival clock, ms. */
  at: number;
  /** The adapter's own term for where it came from: `webhook:CleanRobotStatus`, etc. */
  source: string;
  /** Just the leading word — `webhook` or `poll` — which is what a tally wants. */
  kind: string;
  payload: unknown;
}

/** How many raw records each robot keeps. ~2 minutes at the observed push rate, which
 *  is the window in which "what just happened" is still the question being asked. */
export const RAW_LIMIT = 200;

export type Fleet = Map<string, Robot>;

/** Stable key. Robot ids are unique per site, not globally. */
export const keyOf = (siteId: string, robotId: string) => `${siteId}/${robotId}`;

/**
 * Topic parsing lives in the contract binding (`./contract/fleetComms`), not here. It
 * used to be a hand-written split on `/` that knew `sites`, `robots` and `fleet` as
 * literals — a second reading of the spec, and the same class of mistake as the roster
 * consumer that read `robotId` where `fleet_robots.schema.json` says `robot_id`.
 *
 * Re-exported so callers keep one import for the fleet model.
 */
import { parseTopic } from "./contract/fleetComms";
export { parseTopic };

/**
 * Fold one message into the fleet. Returns a NEW map when something changed, or the
 * same reference when nothing did — so the caller can skip a re-render cheaply.
 *
 * `now` is passed in rather than read from a clock to keep this pure and testable.
 */
export function applyMessage(
  fleet: Fleet,
  topic: string,
  raw: Uint8Array | string,
  now: number,
): Fleet {
  const parsed = parseTopic(topic);
  if (!parsed) return fleet;
  const { siteId, robotId, name } = parsed;

  let value: unknown;
  try {
    value = JSON.parse(typeof raw === "string" ? raw : new TextDecoder().decode(raw));
  } catch {
    // One malformed payload must not stop the fleet updating. It is already recorded.
    return fleet;
  }

  // `telemetry/vendor_raw` is the verbatim vendor echo. It stays OUT of the fleet table —
  // rendering it there would double every update and leak vendor field names into a view
  // that is meant to be platform-neutral — but it is kept per robot for the drill-down,
  // which is the one place the vendor's own words are the point.
  if (robotId !== null && name === "telemetryVendorRaw") {
    return applyRaw(fleet, siteId, robotId, value, now);
  }

  if (robotId === null) {
    return name === "fleetRobots"
      ? applyFleetList(fleet, siteId, value, now)
      : fleet; // other site-level channels are recorded, not rendered
  }

  const key = keyOf(siteId, robotId);
  const prev = fleet.get(key);
  const next: Robot = prev
    ? { ...prev, lastSeen: now }
    : { siteId, robotId, lastSeen: now };

  switch (name) {
    case "statusOnline":
      // A bare JSON boolean, matching the on-prem contract.
      next.online = value === true;
      break;
    case "statusMode": {
      // Two axes since the fleet contract merged the vendor's flat task enum into
      // status/mode: the JOB and its LIFECYCLE. A Go2's mission supervisor fills the
      // same fields, so this branch now renders both platforms.
      const v = value as {
        mode?: string;
        state?: string;
        vendor?: { subState?: number };
      };
      next.mode = v.mode;
      next.modeState = v.state;
      // The vendor's own enum, for the debug readout only. Quarantined under `vendor` in
      // the payload precisely so nothing generic reaches for it.
      next.subState = v.vendor?.subState;
      break;
    }
    case "telemetryBattery": {
      const v = value as { soc?: number };
      // Absent means NOT REPORTED — leave the previous value rather than showing 0,
      // which would read as a robot about to die.
      if (typeof v.soc === "number") next.batteryPct = v.soc;
      break;
    }
    case "telemetryLocalization": {
      const v = value as {
        x?: number;
        y?: number;
        yaw?: number;
        reported_at?: number;
        floor?: string | number;
        frame?: string;
      };
      if (typeof v.x === "number" && typeof v.y === "number") {
        // `frame` is kept because it is load-bearing: a vendor's own map frame is NOT
        // `map`, and plotting one on a Norbot map would put the robot somewhere
        // meaningless. A renderer must check it before drawing.
        next.position = {
          x: v.x,
          y: v.y,
          yaw: v.yaw ?? 0,
          ...(typeof v.reported_at === "number" ? { reportedAt: v.reported_at } : {}),
          floor: v.floor === undefined ? undefined : String(v.floor),
          frame: v.frame,
        };
      }
      break;
    }
    case "statusCapabilities": {
      const v = value as { capabilities?: Capability[]; platform?: string };
      if (Array.isArray(v.capabilities)) next.capabilities = v.capabilities;
      if (typeof v.platform === "string") next.platform = v.platform;
      break;
    }
    case "statusPermitted": {
      const v = value as { permitted?: string[] };
      // Only when it is actually an array. A malformed payload must not read as "the
      // robot permits nothing", which would grey every control with no explanation.
      if (Array.isArray(v.permitted)) next.permitted = v.permitted;
      break;
    }
    case "telemetryCommand": {
      const v = value as CommandEvent;
      if (v && typeof v.name === "string" && typeof v.phase === "string") {
        next.commands = [v, ...(prev?.commands ?? [])].slice(0, COMMAND_LIMIT);
      }
      break;
    }
    case "alarmFault": {
      const v = value as { faulting?: boolean; scram?: boolean; detail?: string | null };
      next.fault = {
        faulting: !!v.faulting,
        scram: !!v.scram,
        detail: v.detail ?? null,
      };
      break;
    }
    default:
      // telemetryTaskLog and anything new: recorded, not part of live state. Still
      // refreshes lastSeen above, because it IS evidence the robot is talking.
      break;
  }

  const out = new Map(fleet);
  out.set(key, next);
  return out;
}

/**
 * A `fleet/robots` listing -> rows in the fleet.
 *
 * This is discovery: it is how a robot first appears at all, before any per-robot
 * telemetry has been asked for. Retained by the adapter, so it also repopulates the list
 * the moment a panel opens rather than leaving it empty until someone triggers a poll.
 *
 * MERGES, never replaces. A listing carries the coarse facts (`online`, `soc`, `model`)
 * and nothing else; overwriting the map would discard live position, task and fault
 * state that arrived by webhook — which is fresher than the listing, not staler.
 */
/**
 * Fold one verbatim vendor record into a robot's ring.
 *
 * MUTATES the ring in place and returns the same map. Every other path here copies, on
 * the principle that a changed model is a new object — but this channel is the highest
 * rate in the system and the ring is not what the fleet table reads, so copying the
 * whole map per raw push would cost real work for a view that does not look at it. The
 * detail view re-reads the ring on its own timer instead.
 */
function applyRaw(fleet: Fleet, siteId: string, robotId: string, value: unknown,
                  now: number): Fleet {
  const v = value as { ts?: number; source?: string; payload?: unknown } | null;
  if (!v || typeof v !== "object") return fleet;
  const r = fleet.get(keyOf(siteId, robotId));
  // No row yet means no robot has been announced — the raw record would have nowhere to
  // hang, and inventing a robot from a debug channel would put a phantom in the table.
  if (!r) return fleet;

  const source = String(v.source ?? "unknown");
  const rec: RawRecord = {
    // OUR arrival clock. `ts` in the payload is also ours (the adapter's), but this is
    // the one that orders what THIS tab saw, which is what a live feed is showing.
    at: now,
    source,
    kind: source.split(":", 1)[0] || "unknown",
    payload: v.payload,
  };

  r.raw ??= [];
  r.raw.unshift(rec);
  if (r.raw.length > RAW_LIMIT) r.raw.length = RAW_LIMIT;

  r.rawCounts ??= {};
  r.rawCounts[rec.kind] = (r.rawCounts[rec.kind] ?? 0) + 1;
  // A raw record IS evidence the robot is talking, so it refreshes liveness like any
  // other message — otherwise a robot whose only traffic is raw looks stale.
  r.lastSeen = now;
  return fleet;
}

function applyFleetList(fleet: Fleet, siteId: string, value: unknown, now: number): Fleet {
  const doc = value as { robots?: unknown; stamp?: number; source?: string };
  if (!Array.isArray(doc?.robots)) return fleet;

  // Seconds in the payload (Python `time.time()`), milliseconds in the browser.
  const asOf = typeof doc.stamp === "number" ? doc.stamp * 1000 : undefined;
  const out = new Map(fleet);
  let changed = false;

  for (const entry of doc.robots as Array<Record<string, unknown>>) {
    // `robot_id`, snake_case, as fleet_robots.schema.json requires. This read `robotId`
    // for a while after the roster payload was renamed in the contract unification, which
    // meant EVERY entry hit the `continue` below and the retained roster populated
    // nothing — invisible, because the same robots arrive again on their own per-robot
    // telemetry topics a moment later. The roster's actual job, answering for a robot
    // that is OFFLINE and therefore publishes nothing, simply never happened.
    const robotId = typeof entry?.robot_id === "string" ? entry.robot_id : null;
    if (!robotId) continue; // an entry we cannot address is not a row we can show
    const key = keyOf(siteId, robotId);
    const prev = out.get(key);
    const next: Robot = prev
      ? { ...prev, lastSeen: now }
      : { siteId, robotId, lastSeen: now };
    if (typeof entry.online === "boolean") next.online = entry.online;
    if (typeof entry.soc === "number") next.batteryPct = entry.soc;
    if (typeof entry.model === "string") next.model = entry.model;
    if (asOf !== undefined) next.asOf = asOf;
    if (typeof doc.source === "string") next.source = doc.source;
    out.set(key, next);
    changed = true;
  }
  return changed ? out : fleet;
}

/** Sorted for a stable render: site, then robot. */
export function sortedRobots(fleet: Fleet): Robot[] {
  return [...fleet.values()].sort((a, b) =>
    a.siteId === b.siteId
      ? a.robotId.localeCompare(b.robotId)
      : a.siteId.localeCompare(b.siteId),
  );
}

/**
 * Whether a robot's data has gone stale.
 *
 * Distinct from `online`. `online` is what the ROBOT last reported; staleness is whether
 * WE are still hearing anything. A robot that reported `online: true` and then went
 * silent — because the adapter died, or our connection dropped — would otherwise show a
 * confident green forever. That is the "silent dead connection that looks live" the plan
 * warns about, one level down.
 */
export function isStale(r: Robot, now: number, ttlMs = 120_000): boolean {
  return now - r.lastSeen > ttlMs;
}
